import { memo, useMemo } from 'react';

import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Presence } from '@/shared/types/presence';

import Collection, { AsItems } from './Collection';
import { SubnavType } from '../SubNav';
import { Collections } from '../SubNavIntent';

import styles from './TreeView.module.scss';

interface CollectionProps {
  collection: string;
  new_collection: string;
}

interface TreeviewProps {
  skeleton?: boolean;
  onCollectionUpdate: ({ id, collection }: { id: string; collection: string }) => void;
  onCollectionSubmit: (data: CollectionProps) => void;
  collections: Collections;
  lastCreatedCollection: string;
  whoIsViewing?: Presence[];
  navTitle?: string;
  subnavType?: SubnavType;
}

const TreeView = ({
  skeleton = true,
  onCollectionUpdate,
  onCollectionSubmit,
  collections,
  lastCreatedCollection,
  whoIsViewing = [],
  navTitle,
  subnavType,
}: TreeviewProps) => {
  const { t } = useTranslation();
  const recommendedCollection = t('intent.recommendations.collection');
  const hasRecommendedCollection = Object.keys(collections).includes(recommendedCollection);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dropActionInNode = (item: any) => {
    if (item.collection !== '') {
      onCollectionUpdate({ id: item.id, collection: '' });
    }
  };

  const [{ isOver }, drop] = useDrop({
    accept: 'treeItem',
    drop: dropActionInNode,
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  const sortedCollections = useMemo(
    () =>
      Object.keys(collections)?.sort((a, b) => {
        if (a === recommendedCollection) {
          return -1;
        }
        if (b === recommendedCollection) {
          return 1;
        }
        if (a === '_orphans') {
          return -1;
        }
        if (b === '_orphans') {
          return 1;
        }
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      }),
    [collections, recommendedCollection]
  );

  const renderSkeletons = () => {
    return (
      <ul>
        {Array(7)
          .fill('')
          .map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>
              <Skeleton className="m-2 h-3.5 w-40" />
            </li>
          ))}
      </ul>
    );
  };
  return (
    <>
      {hasRecommendedCollection && (
        <div className={styles.recommendations}>
          <Collection
            key={recommendedCollection}
            nodeId={recommendedCollection}
            label={recommendedCollection}
            items={collections[recommendedCollection] as AsItems[]}
            onCollectionUpdate={onCollectionUpdate}
            onCollectionSubmit={onCollectionSubmit}
            whoIsViewing={whoIsViewing}
            subnavType={subnavType}
          />
        </div>
      )}
      <span
        ref={(node) => {
          drop(node);
        }}
        className={styles.title}
      >
        <span className="text-sm leading-5 font-medium text-foreground">
          {navTitle || t('common.items')}
        </span>
        {isOver && <span className={styles.dropLine} />}
      </span>
      <div className={styles.collection}>
        {skeleton && renderSkeletons()}
        {sortedCollections?.map((c: string) => {
          const statusColor = collections[c].reduce((acc, item) => {
            if (item?.needsAction && subnavType === 'intents') {
              return 'var(--warning)';
            }
            if (item.hasSuggestions) {
              return 'var(--muted-foreground)';
            }
            return acc;
          }, null);

          if (c !== recommendedCollection) {
            return (
              <Collection
                key={c}
                nodeId={c}
                label={c || t('common.default')}
                items={collections[c] as AsItems[]}
                onCollectionUpdate={onCollectionUpdate}
                onCollectionSubmit={onCollectionSubmit}
                statusColor={statusColor as string}
                isLastCreated={c === lastCreatedCollection}
                whoIsViewing={whoIsViewing}
                subnavType={subnavType}
                collections={collections}
              />
            );
          }
        })}
      </div>
    </>
  );
};

export default memo(TreeView);
